// src/pages/EvaluationPage.js
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useEvaluationData, QuestionTypes } from "../contexts/EvaluationContext";
import { useConfig } from "../contexts/ConfigContext";
import EvaluationIntro from "../components/Evaluation/EvaluationIntro";
import EvaluationSession from "../components/Evaluation/EvaluationSession";
import EvaluationComplete from "../components/Evaluation/EvaluationComplete";
import EvaluationLogin from "../components/Evaluation/EvaluationLogin";

const EvaluationPage = () => {
  const { slug }                    = useParams();
  const { evalData, dispatch }      = useEvaluationData();
  const { config }                  = useConfig();
  const [loading,           setLoading]           = useState(true);
  const [authorized,        setAuthorized]        = useState(false);
  const [sessionState,      setSessionState]      = useState("");
  const [confirmationCode,  setConfirmationCode]  = useState("");
  const [error,             setError]             = useState("");
  
    // Prevent page navigation during active session
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (sessionState === "in-session") {
        const message = "Are you sure you want to leave? Your progress will be lost.";
        e.preventDefault();
        e.returnValue = message; // For Chrome
        return message; // For other browsers
      }
    };

    // Add event listener when session is active
    if (sessionState === "in-session") {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    // Cleanup event listener
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sessionState]);

  // fetch the evaluation metadata (name & password)
  useEffect(() => {
    async function load() {
      try {
        // Get user's timezone
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        const r = await fetch(`${config.apiBase}/evaluation/${slug}`, {
          headers: {
            "X-Timezone": userTimezone,
          }
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || r.statusText);
        dispatch({ type: "SET_EVALUATION", evaluation: j, slug: slug });
      } catch (e) {
        setError(e.message.replace(/</g, "&lt;"));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug, dispatch, config.apiBase]);

  const onSubmitIntroData = async () => {
    // POST the demographic data to the session to start
    const { ageRange, sportSeason, gender, athleteLevel } = evalData.demographics;
    const res = await fetch(
      `${config.apiBase}/session/start`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: evalData.session.id,
          demographics: { ageRange, sportSeason, gender, athleteLevel },
        })
      }
    );
    let { startSequence, endSequence } = evalData.session;
    const j = await res.json();
    if (!res.ok) throw new Error(j.error || res.statusText);
    const groupedQuestions = {};
    const questionsById = {};

    j.questions.forEach(q => {
      const seq = q.sequence;
      startSequence = startSequence < 0 ? seq : Math.min(seq, startSequence);
      endSequence = endSequence < 0 ? seq : Math.max(seq, endSequence);
      // NOTE: sequences do not have to be contiguous and start from 
      // the lowest sequence number and go up to the highest
      if (!groupedQuestions[seq]) groupedQuestions[seq] = {};
      const questionObj = {
        id: q.question_id,
        index: -1,
        text: q.reflective_text,
        helpText: q.help_text,
        attribute_id: q.attribute_id,
        example: q.example_text,
        videoUrl: q.videoUrl,
        questionType: QuestionTypes.indexOf(q.question_type),
        sequence: q.sequence,
        threshold: q.threshold || (q.options.length / 2),
        answerChoices: q.options.map(choice => ({
          ...choice,
          id: choice.scale_option_id,
        })),
      };
      groupedQuestions[seq][q.question_type] = questionObj;
      questionsById[q.question_id] = questionObj;
    });

    dispatch({
      type: "RECEIVE_QUESTIONS",
      questions: groupedQuestions,
      questionsById: questionsById,
      startSequence,
      endSequence,
      numSequences: Object.keys(groupedQuestions).length,
    });
    setSessionState("in-session");
  }

  const onSessionComplete = () => {
    // POST to mark session complete
    setSessionState("completed");
    setConfirmationCode("ABC123XYZ");
  }

  const onAuthorized = (session) => {
    if (session && session.sessionId) {
      dispatch({ type: "START_SESSION", sessionId: session.sessionId });
      setAuthorized(true);
      setSessionState("intro");
    } else {
      setError("Invalid session data received.");
    }
  }

  if (loading) return <p></p>;

  if (!loading && !evalData.evaluation) {
    return <p className="error-text">Evaluation not found.</p>;
  }
  
  const { accountManagerEmail } = evalData.evaluation;

  // password‐gate
  if (!authorized) {
    return (
      <>
        <EvaluationLogin
          evaluation={evalData.evaluation}
          onAuthorized={onAuthorized}
        />
      </>
    );
  }

  // authorized - render the appropriate session state
  return (
    <div className="web-view">
      {error ? (
        <p className="error-text">{error}</p>
      ) : (
        <>
          {sessionState === "intro" && (
            <EvaluationIntro onSubmit={onSubmitIntroData} />
          )}
          {sessionState === "in-session" && (
            <EvaluationSession sessionId={evalData.session.id} onSessionComplete={onSessionComplete} />
          )}
          {sessionState === "completed" && (
            <EvaluationComplete confirmationCode={confirmationCode} managerEmail={accountManagerEmail} />
          )}
        </>
      )}
    </div>
  );
};

export default EvaluationPage;
